import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, User, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import FleetImageOptimized from '../components/FleetImageOptimized';
import ImagePreloader from '../components/ImagePreloader';
import optimizedFleetService from '../lib/optimizedFleetService';
import { FALLBACK_FLEET, VEHICLE_ORDER } from '../constants/fleet';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// Use centralized fallback data
const fallbackFleet = FALLBACK_FLEET;

const Fleet = () => {
  const [fleet, setFleet] = useState(fallbackFleet);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFleet();

    // Listen for fleet data updates from admin
    const handleFleetUpdate = () => {
      fetchFleet();
    };

    window.addEventListener('fleetDataUpdated', handleFleetUpdate);

    return () => {
      window.removeEventListener('fleetDataUpdated', handleFleetUpdate);
    };
  }, []);

  const fetchFleet = async () => {
    try {
      const data = await optimizedFleetService.getVehicles();
      
      if (data && data.length > 0) {
        console.log('🔍 Raw fleet data from database (Services page):', data);
        console.log('🔍 Vehicle names in database (Services page):', data.map(v => ({ name: v.name, status: v.status })));
        
        // Filter for active vehicles and sort them in a consistent order
        // Include ALL vehicles regardless of status for now to debug
        const activeFleet = data.filter(vehicle => {
          const isIncluded = vehicle.status === 'Active' || 
            !vehicle.status || 
            vehicle.status === 'active' ||
            vehicle.status === 'ACTIVE' ||
            vehicle.status === 'Inactive' ||
            vehicle.status === 'inactive' ||
            vehicle.status === 'INACTIVE' ||
            vehicle.status === 'Draft' ||
            vehicle.status === 'draft' ||
            vehicle.status === 'DRAFT' ||
            true; // Include all vehicles for debugging
          
          console.log(`🔍 Vehicle "${vehicle.name}" (${vehicle.type}) - Status: "${vehicle.status}" - Included: ${isIncluded}`);
          return isIncluded;
        });
        
        console.log('🔍 Active fleet after filtering (Services page):', activeFleet);
        
        const vehicleOrder = VEHICLE_ORDER;
        // Sort vehicles according to the defined order
        const sortedFleet = activeFleet.sort((a, b) => vehicleOrder.indexOf(a.name) - vehicleOrder.indexOf(b.name));
        
        console.log('🔍 Sorted fleet (Services page):', sortedFleet);
        console.log('🔍 Fleet count (Services page):', sortedFleet.length);
        
        const transformedFleet = sortedFleet.map(vehicle => {
          return {
            name: vehicle.name,
            type: vehicle.type,
            description: vehicle.description || `Luxury ${vehicle.type} for your transportation needs.`,
            image_url: vehicle.image_url,
            image_url_fallback: vehicle.image_url_fallback || vehicle.image_url,
            alt: vehicle.name,
            capacity: `Up to ${vehicle.capacity || 4}`,
            luggage: `Up to ${Math.floor((vehicle.capacity || 4) * 0.5)}`,
          };
        });
        
        setFleet(transformedFleet);
      } else {
        setFleet(fallbackFleet);
      }
    } catch (error) {
      console.error('Error fetching fleet:', error);
      setFleet(fallbackFleet);
    } finally {
      setLoading(false);
    }
  };

  const memoizedFleet = useMemo(() => fleet, [fleet]);
  
  // Get critical images for preloading (only first 2 for performance)
  const criticalImages = useMemo(() => {
    return memoizedFleet
      .slice(0, 2)
      .map(vehicle => vehicle.image_url)
      .filter(url => url && !url.startsWith('data:'));
  }, [memoizedFleet]);
  
  return (
    <>
      <Helmet>
        <title>Our Fleet - Akiblackcar</title>
        <meta name="description" content="Explore the premier fleet of Akiblackcar. We offer a range of luxury vehicles including SUVs, Sedans, Stretch Limousines, Sprinter Vans, and Coach Buses to meet all your transportation needs in the DMV area." />
      </Helmet>
      <ImagePreloader images={criticalImages} />
      <motion.div initial="hidden" animate="visible" variants={fadeIn} className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-brand-gold">Our Premier Fleet</h1>
          <p className="mt-4 text-lg text-gray-300 max-w-3xl mx-auto">
            Luxury, comfort, and reliability across our entire range of meticulously maintained vehicles. Find the perfect ride for any occasion.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {memoizedFleet.map((vehicle, index) => {
            return (
              <motion.div key={index} variants={fadeIn} custom={index} initial="hidden" animate="visible" transition={{ delay: index * 0.1 }}>
                <Card className="bg-brand-charcoal border-brand-gold/20 overflow-hidden h-full flex flex-col">
                  <CardHeader className="p-0">
                    <FleetImageOptimized
                      src={vehicle.image_url}
                      alt={vehicle.alt}
                      vehicleType={vehicle.type}
                      vehicleName={vehicle.name}
                      fallbackSrc={vehicle.image_url_fallback}
                      className="w-full h-56 object-cover"
                      priority={index < 2} // First 2 images are priority
                    />
                </CardHeader>
                <CardContent className="p-6 flex flex-col flex-grow">
                  <CardTitle className="text-2xl font-serif text-white mb-3">{vehicle.name}</CardTitle>
                  <p className="text-gray-400 mb-4 flex-grow">{vehicle.description}</p>
                  <div className="flex items-center text-brand-gold space-x-6 mb-6">
                    <div className="flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      <span>{vehicle.capacity}</span>
                    </div>
                    <div className="flex items-center">
                      <Briefcase className="w-5 h-5 mr-2" />
                      <span>{vehicle.luggage}</span>
                    </div>
                  </div>
                  <Link to="/free-estimate" className="mt-auto">
                    <Button size="lg" className="w-full bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold text-lg">
                      Get Your Free Estimate <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
            );
          })}
        </div>
      </motion.div>
    </>
  );
};

export default Fleet;